import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Printer, Zap, Pencil, FileText, CreditCard, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tinkah_token")}` }
});

const statusColor = (s) => {
  if (s === "completed") return "bg-[#4B6F44] text-white border-[#4B6F44]";
  if (s === "in_progress") return "bg-[#D6AD60]/20 text-[#5C4D24] border-[#D6AD60]";
  return "bg-transparent text-[#9F7D23] border-[#9F7D23]";
};

const measureLabels = {
  shoulder: "Shoulder", bust: "Bust", hips: "Hips", length: "Length",
  sleeve_length: "Sleeve Length", armhole: "Armhole", cuff: "Cuff",
  button_placement: "Button Placement", sheila_length: "Sheila Length",
};

export default function OrderDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentType, setPaymentType] = useState("partial");

  useEffect(() => {
    axios.get(`${API}/orders/${id}`, getAuthHeaders())
      .then((r) => setOrder(r.data))
      .catch(() => toast.error("Order not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/orders/${id}/status`, { status }, getAuthHeaders());
      setOrder((prev) => ({ ...prev, status }));
      toast.success(`Status updated to ${status.replace("_", " ")}`);
    } catch { toast.error("Failed to update status"); }
  };

  const recordPayment = async () => {
    const balanceDue = order.financial?.balance_due || 0;
    let amount = paymentType === "full" ? balanceDue : parseFloat(paymentAmount) || 0;
    
    if (amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (amount > balanceDue) {
      amount = balanceDue;
    }
    
    const newAdvance = (order.financial?.advance_paid || 0) + amount;
    const newBalance = (order.financial?.total_price || 0) - newAdvance;
    
    try {
      await axios.put(`${API}/orders/${id}/payment`, { 
        advance_paid: newAdvance,
        balance_due: Math.max(0, newBalance)
      }, getAuthHeaders());
      
      setOrder((prev) => ({
        ...prev,
        financial: {
          ...prev.financial,
          advance_paid: newAdvance,
          balance_due: Math.max(0, newBalance)
        }
      }));
      
      toast.success(newBalance <= 0 ? "Payment complete! Order fully paid." : `Payment of ${amount} AED recorded`);
      setPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentType("partial");
    } catch { 
      toast.error("Failed to record payment"); 
    }
  };

  const openPaymentDialog = () => {
    setPaymentAmount("");
    setPaymentType("partial");
    setPaymentDialogOpen(true);
  };

  useEffect(() => {
    const afterPrint = () => {
      setPrintMode(null);
      const el = document.getElementById("tinkah-print-size");
      if (el) el.remove();
    };
    window.addEventListener("afterprint", afterPrint);
    return () => window.removeEventListener("afterprint", afterPrint);
  }, []);

  useEffect(() => {
    if (!printMode) return;
    
    // Force DOM update before print
    const printSection = document.querySelector(`[data-testid="print-${printMode === 'job-card' ? 'job-card' : 'confirmation'}"]`);
    if (printSection) {
      printSection.classList.add('print-active');
    }
    
    if (printMode === "job-card") {
      let s = document.getElementById("tinkah-print-size");
      if (!s) {
        s = document.createElement("style");
        s.id = "tinkah-print-size";
        s.textContent = "@media print { @page { size: A5; margin: 8mm; } }";
        document.head.appendChild(s);
      }
    } else {
      const s = document.getElementById("tinkah-print-size");
      if (s) s.remove();
    }
    
    // Give more time for DOM to update
    const t = setTimeout(() => {
      window.print();
    }, 500);
    
    return () => {
      clearTimeout(t);
      if (printSection) {
        printSection.classList.remove('print-active');
      }
    };
  }, [printMode]);

  const handlePrint = (mode) => {
    setPrintMode(mode);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" /></div>;
  if (!order) return <div className="text-center py-16 text-[#8B7536]">Order not found</div>;

  const items = order.items || [];
  const grandTotal = order.financial?.total_price || items.reduce((s, i) => s + (i.quantity || 1) * (i.price_per_piece || 0), 0);

  return (
    <>
      <div className="no-print space-y-6 animate-fade-in-up" data-testid="order-detail-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/orders" data-testid="back-to-orders" className="text-[#5C4D24] hover:text-[#9F7D23] transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h2 className="font-['Space_Mono'] text-2xl tracking-tight uppercase text-[#5C4D24]">{order.order_number}</h2>
              <p className="text-sm text-[#8B7536]">{order.customer?.name}</p>
            </div>
            {order.financial?.is_urgent && <Zap className="w-5 h-5 text-[#9F3A3A]" />}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={order.status} onValueChange={updateStatus}>
              <SelectTrigger className="w-36 rounded-none font-['Space_Mono'] text-xs uppercase tracking-widest" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => navigate(`/orders/${id}/edit`)} data-testid="edit-order-btn" className="flex items-center gap-2 px-4 py-2 border border-[#9F7D23] text-[#9F7D23] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#9F7D23] hover:text-white transition-all">
              <Pencil className="w-4 h-4" /> Edit
            </button>
            <button onClick={() => handlePrint("job-card")} data-testid="print-job-card-btn" className="flex items-center gap-2 px-4 py-2 bg-[#5C4D24] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#3d3218] transition-all active:scale-95">
              <Printer className="w-4 h-4" /> Job Card
            </button>
            <button onClick={() => handlePrint("confirmation")} data-testid="print-confirmation-btn" className="flex items-center gap-2 px-4 py-2 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95">
              <FileText className="w-4 h-4" /> Confirmation
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#D6D6CF] p-6" data-testid="customer-section">
            <h3 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23] mb-4">Customer</h3>
            <div className="space-y-2 text-sm text-[#5C4D24]">
              <p><span className="text-[#8B7536]">Name:</span> {order.customer?.name}</p>
              {order.customer?.profile_name && (
                <p><span className="text-[#8B7536]">For:</span> <span className="font-medium text-[#9F7D23]">{order.customer.profile_name}</span></p>
              )}
              <p><span className="text-[#8B7536]">Contact:</span> {order.customer?.contact}</p>
              <p><span className="text-[#8B7536]">Address:</span> {order.customer?.address}</p>
            </div>
          </div>
          <div className="bg-white border border-[#D6D6CF] p-6" data-testid="financial-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23]">Financial</h3>
              {order.financial?.balance_due <= 0 ? (
                <span className="flex items-center gap-1 px-3 py-1 bg-[#4B6F44] text-white text-xs font-['Space_Mono'] tracking-widest uppercase">
                  <CheckCircle className="w-3 h-3" /> Fully Paid
                </span>
              ) : (
                <button 
                  onClick={openPaymentDialog}
                  data-testid="record-payment-btn"
                  className="flex items-center gap-1 px-3 py-1 bg-[#9F7D23] text-white text-xs font-['Space_Mono'] tracking-widest uppercase hover:bg-[#8A6D1F] transition-all"
                >
                  <CreditCard className="w-3 h-3" /> Record Payment
                </button>
              )}
            </div>
            <div className="space-y-2 text-sm text-[#5C4D24]">
              <p><span className="text-[#8B7536]">Total:</span> <span className="font-bold">{grandTotal} AED</span></p>
              <p><span className="text-[#8B7536]">Paid:</span> {order.financial?.advance_paid} AED</p>
              {order.financial?.balance_due > 0 ? (
                <p><span className="text-[#8B7536]">Balance Due:</span> <span className="text-[#9F3A3A] font-bold">{order.financial?.balance_due} AED</span></p>
              ) : (
                <p className="text-[#4B6F44] font-bold">No balance due</p>
              )}
              <p><span className="text-[#8B7536]">Method:</span> {order.financial?.payment_method}</p>
              <p><span className="text-[#8B7536]">Delivery:</span> {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : "TBD"}</p>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white border border-[#D6D6CF] p-6" data-testid="items-section">
          <h3 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23] mb-4">Items ({items.length})</h3>
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between py-3 border-b border-[#D6D6CF] last:border-0 text-sm">
              <div>
                <span className="font-medium text-[#5C4D24]">{item.garment_type}</span>
                {item.collection_name && <span className="text-[#8B7536]"> - {item.collection_name}</span>}
                <span className="text-[#8B7536]"> / {item.fabric_name || "N/A"}</span>
                {item.custom_requests && <p className="text-xs text-[#8B7536] italic mt-1">{item.custom_requests}</p>}
              </div>
              <div className="text-right font-['Space_Mono']">
                <span className="text-[#5C4D24]">{item.quantity} x {item.price_per_piece}</span>
                <span className="font-bold text-[#5C4D24] ml-2">= {(item.quantity * item.price_per_piece).toFixed(0)} AED</span>
              </div>
            </div>
          ))}
        </div>

        {/* Measurements */}
        <div className="bg-white border border-[#D6D6CF] p-6" data-testid="measurements-section">
          <h3 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23] mb-4">Measurements (cm)</h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
            {Object.entries(measureLabels).map(([key, label]) => (
              <div key={key} className="text-center border border-[#D6D6CF] p-3">
                <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">{label}</p>
                <p className="font-['Space_Mono'] text-xl text-[#5C4D24] mt-1">{order.measurements?.[key] || 0}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PRINT: Job Card (A5, NO financials) ── */}
      <div className={`print-section ${printMode === "job-card" ? "print-active" : ""}`} data-testid="print-job-card">
        <div style={{ fontFamily: "'Space Mono', monospace", color: "#5C4D24" }}>
          <div style={{ textAlign: "center", borderBottom: "2px solid #9F7D23", paddingBottom: 12, marginBottom: 16 }}>
            <img src="/tinkah-logo.png" alt="TINKAH" style={{ height: 50, margin: "0 auto", display: "block" }} />
            <p style={{ fontSize: 9, letterSpacing: "0.3em", marginTop: 8, textTransform: "uppercase" }}>Workshop Job Card</p>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontSize: 11 }}>
            <div>
              <p><strong>Order:</strong> {order.order_number}</p>
              <p><strong>Customer:</strong> {order.customer?.name}</p>
              {order.customer?.profile_name && <p><strong>For:</strong> {order.customer.profile_name}</p>}
            </div>
            <div style={{ textAlign: "right" }}>
              <p><strong>Date:</strong> {new Date(order.order_date).toLocaleDateString()}</p>
              <p><strong>Delivery:</strong> {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : "TBD"}</p>
            </div>
          </div>
          {order.financial?.is_urgent && (
            <div style={{ border: "2px solid #9F3A3A", padding: "6px 12px", marginBottom: 12, textAlign: "center", fontSize: 12, fontWeight: "bold", color: "#9F3A3A" }}>URGENT ORDER</div>
          )}
          <h3 style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8, borderBottom: "1px solid #D6D6CF", paddingBottom: 4 }}>Items</h3>
          {items.map((item, idx) => (
            <div key={idx} style={{ fontSize: 11, marginBottom: 8, paddingBottom: 6, borderBottom: "1px dotted #D6D6CF" }}>
              <p><strong>{item.garment_type}</strong> {item.collection_name ? `- ${item.collection_name}` : ""} / {item.fabric_name || "N/A"} (Qty: {item.quantity})</p>
              {item.custom_requests && <p style={{ fontStyle: "italic", fontSize: 10, marginTop: 2 }}>Requests: {item.custom_requests}</p>}
            </div>
          ))}
          <h3 style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 12, marginBottom: 8, borderBottom: "1px solid #D6D6CF", paddingBottom: 4 }}>Measurements (cm)</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 11 }}>
            <tbody>
              {Object.entries(measureLabels).map(([key, label]) => (
                <tr key={key} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "4px 0", textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</td>
                  <td style={{ padding: "4px 0", fontWeight: "bold", textAlign: "right" }}>{order.measurements?.[key] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, fontSize: 11 }}>
            <div style={{ width: 14, height: 14, border: "2px solid #5C4D24" }}></div>
            <span>Fabric and measurements verified by technician</span>
          </div>
          <div style={{ borderTop: "1px solid #D6D6CF", paddingTop: 10, marginTop: 16 }}>
            <p style={{ fontSize: 9, fontStyle: "italic", textAlign: "center", opacity: 0.8 }}>Every Tinkah piece is handcrafted. Minor variations are part of the artistry.</p>
            <p style={{ fontSize: 8, textAlign: "center", letterSpacing: "0.25em", textTransform: "uppercase", marginTop: 6, opacity: 0.6 }}>Bespoke Embroidery | Crafted with Intention</p>
          </div>
        </div>
      </div>

      {/* ── PRINT: Customer Confirmation (with financials) ── */}
      <div className={`print-section ${printMode === "confirmation" ? "print-active" : ""}`} data-testid="print-confirmation">
        <div style={{ fontFamily: "'Space Mono', monospace", color: "#5C4D24" }}>
          <div style={{ textAlign: "center", borderBottom: "2px solid #9F7D23", paddingBottom: 16, marginBottom: 24 }}>
            <img src="/tinkah-logo.png" alt="TINKAH" style={{ height: 60, margin: "0 auto", display: "block" }} />
            <p style={{ fontSize: 10, letterSpacing: "0.3em", marginTop: 8, textTransform: "uppercase" }}>Order Confirmation</p>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, fontSize: 12 }}>
            <div>
              <p><strong>Order:</strong> {order.order_number}</p>
              <p><strong>Customer:</strong> {order.customer?.name}</p>
              {order.customer?.profile_name && <p><strong>For:</strong> {order.customer.profile_name}</p>}
              <p><strong>Contact:</strong> {order.customer?.contact}</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <p><strong>Date:</strong> {new Date(order.order_date).toLocaleDateString()}</p>
              <p><strong>Delivery:</strong> {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : "TBD"}</p>
            </div>
          </div>
          <h3 style={{ fontSize: 14, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 12 }}>Order Items</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #9F7D23" }}>
                <th style={{ textAlign: "left", padding: "8px 0", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 }}>Item</th>
                <th style={{ textAlign: "center", padding: "8px 0", fontSize: 10 }}>Qty</th>
                <th style={{ textAlign: "right", padding: "8px 0", fontSize: 10 }}>Price</th>
                <th style={{ textAlign: "right", padding: "8px 0", fontSize: 10 }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #D6D6CF" }}>
                  <td style={{ padding: "8px 0" }}>{item.garment_type} {item.collection_name ? `- ${item.collection_name}` : ""}<br /><span style={{ fontSize: 10, opacity: 0.7 }}>Fabric: {item.fabric_name || "N/A"}</span></td>
                  <td style={{ textAlign: "center", padding: "8px 0" }}>{item.quantity}</td>
                  <td style={{ textAlign: "right", padding: "8px 0" }}>{item.price_per_piece} AED</td>
                  <td style={{ textAlign: "right", padding: "8px 0", fontWeight: "bold" }}>{(item.quantity * item.price_per_piece).toFixed(0)} AED</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ borderTop: "2px solid #9F7D23", paddingTop: 12, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span>Total</span><span style={{ fontWeight: "bold" }}>{grandTotal} AED</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span>Advance Paid</span><span>{order.financial?.advance_paid} AED</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: "bold", paddingTop: 8, borderTop: "1px solid #D6D6CF" }}>
              <span>Balance Due</span><span style={{ color: "#9F3A3A" }}>{order.financial?.balance_due} AED</span>
            </div>
            <p style={{ fontSize: 10, marginTop: 4, opacity: 0.7 }}>Payment Method: {order.financial?.payment_method?.toUpperCase()}</p>
          </div>
          <div style={{ borderTop: "1px solid #D6D6CF", paddingTop: 16, textAlign: "center" }}>
            <p style={{ fontSize: 11, marginBottom: 8 }}>Thank you for choosing Tinkah.</p>
            <p style={{ fontSize: 10, marginBottom: 4 }}>For inquiries, please contact us:</p>
            <p style={{ fontSize: 10, marginBottom: 2 }}>Email: tinkahembroidery@outlook.com</p>
            <p style={{ fontSize: 10, marginBottom: 12 }}>Phone: 058-8676735</p>
            <p style={{ fontSize: 9, letterSpacing: "0.25em", textTransform: "uppercase", opacity: 0.6 }}>Bespoke Embroidery | Crafted with Intention</p>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-sm rounded-none border-[#D6D6CF]">
          <DialogHeader>
            <DialogTitle className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#F5F5F0] p-4 text-center">
              <p className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Balance Due</p>
              <p className="font-['Space_Mono'] text-3xl text-[#9F3A3A] font-bold">{order?.financial?.balance_due} AED</p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setPaymentType("full")}
                className={`flex-1 py-3 font-['Space_Mono'] text-xs tracking-widest uppercase transition-all ${
                  paymentType === "full" 
                    ? "bg-[#4B6F44] text-white" 
                    : "border border-[#D6D6CF] text-[#5C4D24] hover:border-[#9F7D23]"
                }`}
                data-testid="payment-full-btn"
              >
                Full Payment
              </button>
              <button
                onClick={() => setPaymentType("partial")}
                className={`flex-1 py-3 font-['Space_Mono'] text-xs tracking-widest uppercase transition-all ${
                  paymentType === "partial" 
                    ? "bg-[#9F7D23] text-white" 
                    : "border border-[#D6D6CF] text-[#5C4D24] hover:border-[#9F7D23]"
                }`}
                data-testid="payment-partial-btn"
              >
                Partial
              </button>
            </div>

            {paymentType === "partial" && (
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Amount (AED)</Label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono'] text-xl"
                  data-testid="payment-amount-input"
                  max={order?.financial?.balance_due}
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setPaymentDialogOpen(false)}
                className="flex-1 py-3 border border-[#D6D6CF] text-[#5C4D24] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#F5F5F0] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={recordPayment}
                data-testid="confirm-payment-btn"
                className="flex-1 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
